import { useCallback, useEffect, useRef, useState } from "react";
import { useConnectionAdapter } from "../../../services/ConnectionRegistryProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { useEngageClient } from "./useEngageClient";
import type {
  EngageAttachmentUpload,
  EngageClient,
  EngageCreateResponse,
  EngageJob,
  EngageJobHeader,
  EngageJobModel,
  EngageJobType,
  EngageRequestor,
  EngageSite,
} from "../../../chat/services/workflowApi";
import { readMigratedSessionPref } from "../../../shared/sessionPref";

// AIVA-6685 (V-08): these preferences/caches live in sessionStorage, not
// localStorage — see shared/sessionPref.ts. They are taskpane-only, so a
// per-window store is safe; legacy localStorage values are migrated once.
const SITE_STORAGE_KEY = "aiva_engage_site";
const ORG_STORAGE_KEY = "aiva_engage_org_id";

interface JobSubmitInput {
  readonly site: string;
  readonly jobType: string;
  readonly title: string;
  readonly instructions?: string;
  readonly dueDate?: string;
  readonly requestorEmail?: string;
  readonly userEmail?: string;
  // The Engage COGNITO user's email — recorded as `POSTED_BY` on SaveAttachments.
  // Must be the Cognito identity, not the M365/Office `userEmail`. Falls back to
  // `userEmail` when unavailable (AIVA-6989).
  readonly postedByEmail?: string;
  // Each attachment carries EITHER `text` (encoded to base64 here) or
  // pre-encoded `base64` bytes (e.g. an email file attachment, AIVA-6989).
  readonly attachments?: ReadonlyArray<{
    name: string;
    text?: string;
    base64?: string;
    mimeType?: string;
  }>;
  // Optional OneDrive staging for attachments (AIVA-6989). Supplied by the UI
  // when a Graph token is available; resolves to a read-only URL for the staged
  // file (a view-only sharing link) that SaveAttachments references in URL mode.
  readonly uploadToDrive?: (
    folderPath: string,
    fileName: string,
    base64: string,
    contentType: string,
  ) => Promise<{ url: string; size: number }>;
}

interface JobSubmitResult {
  readonly jobNo: string;
  readonly sla?: string;
  readonly attachmentsSaved: number;
  readonly attachmentError: boolean;
}

function pickJobNo(resp: unknown): string {
  if (!resp || typeof resp !== "object") return "";
  const r = resp as Record<string, unknown>;
  // On success Engage's ResponseModel returns the job number as `result`
  // (a plain string) — see the old plugin's CreateJob.tsx#L388. Older
  // wrappers also surface joB_NO/jobNo so we check both shapes.
  if (r.success === true && typeof r.result === "string") return r.result;
  if (typeof r.joB_NO === "string") return r.joB_NO;
  if (typeof r.jobNo === "string") return r.jobNo;
  if (r.result && typeof r.result === "object") {
    const inner = r.result as Record<string, unknown>;
    if (typeof inner.joB_NO === "string") return inner.joB_NO;
    if (typeof inner.jobNo === "string") return inner.jobNo;
  }
  return "";
}

/**
 * Engage returns 200 OK with `{success: false, result: "<message>"}` for
 * server-side validation failures. Surface that string so the UI shows
 * something useful instead of the generic "did not return a job number".
 */
function pickErrorMessage(resp: unknown): string | undefined {
  if (!resp || typeof resp !== "object") return undefined;
  const r = resp as Record<string, unknown>;
  if (r.success === false) {
    if (typeof r.result === "string") return r.result;
    if (typeof r.message === "string") return r.message;
  }
  return undefined;
}

function pickSla(resp: unknown): string | undefined {
  if (!resp || typeof resp !== "object") return undefined;
  const r = resp as Record<string, unknown>;
  if (r.result && typeof r.result === "object") {
    const inner = r.result as Record<string, unknown>;
    if (typeof inner.sla === "string") return inner.sla;
  }
  return undefined;
}

/**
 * Validates the optional due date. Returns a user-facing message when it is
 * earlier than today, else undefined. The date input yields a local
 * "YYYY-MM-DD" string, so we compare at day granularity — a due date of today
 * is allowed. Guarding the submit here means a past date fails with a clear
 * message instead of Engage's opaque rejection, which the UI would otherwise
 * surface as the misleading generic "did not return a job number" (AIVA-6847).
 */
function validateDueDate(dueDate: string | undefined): string | undefined {
  if (!dueDate) return undefined;
  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due.getTime() < today.getTime()) {
    return "Due date cannot be earlier than today.";
  }
  return undefined;
}

/**
 * Extracts the prM01/vaL01 … prM20/vaL20 field/value pairs from a
 * GetRequestorByEmail result row into JobHeaderDTO entries, stripping the
 * "FLD_" prefix Engage uses on the prM* names.
 */
function requestorHeaders(req: EngageRequestor): EngageJobHeader[] {
  const headers: EngageJobHeader[] = [];
  const rec = req as Record<string, unknown>;
  for (let i = 1; i <= 20; i++) {
    const idx = String(i).padStart(2, "0");
    const fieldRaw = rec[`prM${idx}`];
    const valueRaw = rec[`vaL${idx}`];
    if (typeof fieldRaw !== "string" || !fieldRaw) continue;
    let value: string | null = null;
    if (typeof valueRaw === "string" && valueRaw !== "") value = valueRaw;
    else if (typeof valueRaw === "number" || typeof valueRaw === "boolean") value = String(valueRaw);
    if (value === null) continue;
    headers.push({
      field: fieldRaw.replace(/^FLD_/, ""),
      value,
    });
  }
  return headers;
}

/**
 * Sanitises a header value the way the prototype does — strips angle brackets
 * (Engage's input validation rejects them) and clamps long fields so the
 * payload doesn't exceed Engage's per-field limits.
 */
function safeHeader(value: string, maxLen: number): string {
  return value.replace(/</g, "[").replace(/>/g, "]").slice(0, maxLen);
}

/** Base64-encode UTF-8 text (mirrors the prototype's btoa(unescape(encodeURIComponent(...)))). */
function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = "";
  for (const b of bytes) bin += String.fromCodePoint(b);
  return btoa(bin);
}

/**
 * Resolve the requestor row: try each candidate email on the chosen job type,
 * then probe every OTHER job type with the first email (prototype behaviour).
 */
async function resolveRequestor(
  client: EngageClient,
  site: string,
  jobType: string,
  emailsToTry: readonly string[],
): Promise<EngageRequestor | null> {
  for (const email of emailsToTry) {
    const reqs = await client
      .getRequestorByEmail(site, jobType, email)
      .catch(() => [] as EngageRequestor[]);
    if (Array.isArray(reqs) && reqs.length > 0) return reqs[0]!;
  }
  if (emailsToTry.length === 0) return null;
  const probeEmail = emailsToTry[0]!;
  const types = await client.getJobTypesByUser(site).catch(() => [] as EngageJobType[]);
  for (const t of types) {
    if (t.joB_TYPE === jobType) continue;
    const reqs = await client
      .getRequestorByEmail(site, t.joB_TYPE, probeEmail)
      .catch(() => [] as EngageRequestor[]);
    if (Array.isArray(reqs) && reqs.length > 0) return reqs[0]!;
  }
  return null;
}

/** Build the JobModelDTO headers from the requestor + form input. */
function buildJobHeaders(
  requestor: EngageRequestor | null,
  orgId: string,
  input: JobSubmitInput,
): EngageJobHeader[] {
  const headers: EngageJobHeader[] = [
    ...(requestor ? requestorHeaders(requestor) : []),
    { field: "REQUESTED_DATE", value: new Date().toISOString() },
    { field: "JOB_TITLE", value: safeHeader(input.title, 80) },
  ];
  if (orgId) headers.push({ field: "ORGANIZATIONSID", value: orgId });
  if (input.instructions) {
    headers.push({ field: "INSTRUCTIONS", value: safeHeader(input.instructions, 10000) });
  }
  if (input.dueDate) {
    // The Create Job form captures a date only (no time). Send the selected day
    // as local start-of-day rather than a hardcoded T17:00:00 — otherwise Engage
    // stores/shows a fabricated evening Due Time (e.g. 10:30 PM in IST) the user
    // never entered (AIVA-6932). Anchoring to local midnight keeps the calendar
    // date correct when Engage renders it back in the same timezone.
    headers.push({ field: "DUE_DATE", value: new Date(`${input.dueDate}T00:00:00`).toISOString() });
  }
  return headers;
}

/** POST CreateInternal, falling back to /Create on transport failure. */
async function createJob(
  client: EngageClient,
  input: JobSubmitInput,
  orgId: string,
  user: string,
  jobModel: EngageJobModel,
): Promise<EngageCreateResponse> {
  try {
    const created = await client.createInternal(input.site, input.jobType, orgId, jobModel);
    logConnectorSuccess("engage", "create-internal");
    return created;
  } catch (createErr) {
    logConnectorFailure("engage", createErr, "create-internal");
    return client.createFallback(user, input.site, input.jobType, jobModel);
  }
}

type DriveUpload = (
  folderPath: string,
  fileName: string,
  base64: string,
  contentType: string,
) => Promise<{ url: string; size: number }>;

/**
 * Upload each checked attachment to OneDrive, then register it on the job via
 * SaveAttachments in URL mode. Engage's JSON SaveAttachments endpoint only
 * stores a URL reference — File mode reads the bytes from a multipart stream a
 * JSON body can't carry — so every attachment must first be hosted somewhere
 * with a URL. Best-effort: never throws (AIVA-6989).
 */
async function uploadAttachments(
  client: EngageClient,
  jobNo: string,
  user: string,
  attachments: ReadonlyArray<{ name: string; text?: string; base64?: string; mimeType?: string }>,
  uploadToDrive?: DriveUpload,
): Promise<{ attachmentsSaved: number; attachmentError: boolean }> {
  if (attachments.length === 0) return { attachmentsSaved: 0, attachmentError: false };

  // No OneDrive uploader → nowhere to host the file, and this endpoint cannot
  // accept inline bytes, so the attachment cannot be saved (AIVA-6989).
  if (!uploadToDrive) return { attachmentsSaved: 0, attachmentError: true };

  const prepared = attachments.map((a) => ({
    // Byte attachments arrive pre-encoded; text attachments are encoded here —
    // never re-encode already-base64 bytes (AIVA-6989).
    name: a.name.replace(/[^a-zA-Z0-9 .-]/g, " "),
    file: a.base64 ?? toBase64(a.text ?? ""),
    type: a.mimeType || "text/plain",
  }));

  // Stage every file in OneDrive; the uploader returns a read-only URL Engage
  // can reference (a view-only sharing link, so it opens in a viewer instead of
  // downloading — AIVA-6989/7093).
  const staged = await Promise.all(
    prepared.map(async (p) => {
      try {
        const { url } = await uploadToDrive(`AiVA/Engage/${jobNo}`, p.name, p.file, p.type);
        logConnectorSuccess("engage", "onedrive-upload");
        return { name: p.name, url };
      } catch (e) {
        logConnectorFailure("engage", e, "onedrive-upload");
        return { name: p.name, url: null as string | null };
      }
    }),
  );

  const uploaded = staged.filter((s): s is { name: string; url: string } => Boolean(s.url));
  // Nothing hosted → nothing to register.
  if (uploaded.length === 0) return { attachmentsSaved: 0, attachmentError: true };

  const payloads: EngageAttachmentUpload[] = uploaded.map((s) => ({
    JOB_NO: jobNo,
    URL: s.url,
    FILE_NAME: s.name,
    // Engage stores the URL as a link under the job's Original folder (it does
    // NOT download it). FILE_TYPE must be the mode literal "URL" — any MIME/other
    // value matches neither server branch and the save fails (AIVA-6989).
    FILE_LOCATION: "Original",
    FILE_TYPE: "URL",
    POSTED_BY: user,
    IS_SELECTED: true,
  }));

  try {
    const resp = await client.saveAttachments(payloads);
    // Engage returns HTTP 200 with { success:false, result:"<message>" } when it
    // rejects the save, so a non-throwing call is NOT proof it worked — check the
    // success flag before reporting the attachments as sent (AIVA-6989).
    const r = resp as { success?: boolean; result?: unknown } | null;
    if (r?.success === false) {
      const msg = typeof r.result === "string" ? r.result : "SaveAttachments returned success:false";
      logConnectorFailure("engage", new Error(msg), "save-attachments");
      return { attachmentsSaved: 0, attachmentError: true };
    }
    // Any file that failed to upload is a partial error.
    logConnectorSuccess("engage", "save-attachments");
    return { attachmentsSaved: payloads.length, attachmentError: uploaded.length < staged.length };
  } catch (e) {
    logConnectorFailure("engage", e, "save-attachments");
    return { attachmentsSaved: 0, attachmentError: true };
  }
}

export function useEngageWorkflow() {
  const adapter = useConnectionAdapter("engage");
  const client = useEngageClient();

  const [connected, setConnected] = useState<boolean>(adapter?.isConnected() ?? false);
  const [sites, setSites] = useState<EngageSite[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>(
    () => readMigratedSessionPref(SITE_STORAGE_KEY) ?? "",
  );
  const [jobTypes, setJobTypes] = useState<EngageJobType[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingJobTypes, setLoadingJobTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sitesAttempted, setSitesAttempted] = useState(false);

  /* ── Track connection state ── */
  useEffect(() => {
    if (!adapter) {
      setConnected(false);
      return;
    }
    setConnected(adapter.isConnected());
    return adapter.onTokenChange((t) => {
      setConnected(!!t);
      // New token → allow another auto-load attempt
      if (t) setSitesAttempted(false);
    });
  }, [adapter]);

  /* ── Load sites once connected ── */
  const loadSites = useCallback(async () => {
    if (!connected) return;
    setLoadingSites(true);
    setError(null);
    try {
      const list = await client.getSitesByUser();
      setSites(list);
      logConnectorSuccess("engage", "load-sites");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load sites";
      setError(msg);
      logConnectorFailure("engage", e, "load-sites");
    } finally {
      setLoadingSites(false);
      setSitesAttempted(true);
    }
  }, [client, connected]);

  const reloadSites = useCallback(async () => {
    setSitesAttempted(false);
    await loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (connected && !sitesAttempted && !loadingSites) {
      loadSites().catch(() => undefined);
    }
  }, [connected, sitesAttempted, loadingSites, loadSites]);

  /* ── Load job types when site changes ── */
  const loadJobTypes = useCallback(
    async (site: string) => {
      if (!connected || !site) {
        setJobTypes([]);
        return;
      }
      setLoadingJobTypes(true);
      setError(null);
      try {
        const list = await client.getJobTypesByUser(site);
        setJobTypes(list);
        logConnectorSuccess("engage", "load-job-types");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load job types";
        setError(msg);
        logConnectorFailure("engage", e, "load-job-types");
      } finally {
        setLoadingJobTypes(false);
      }
    },
    [client, connected],
  );

  useEffect(() => {
    if (selectedSite) {
      globalThis.sessionStorage?.setItem(SITE_STORAGE_KEY, selectedSite);
      loadJobTypes(selectedSite).catch(() => undefined);
    }
  }, [selectedSite, loadJobTypes]);

  /* ── Submit a job — mirrors the prototype's handleEngageSubmit flow ──
   *
   * 1. Resolve the requestor: try each candidate email (requestor, then user)
   *    on the chosen job type; if none match, probe every OTHER job type for
   *    the site with the first email until a requestor row is found. Never
   *    block submit on a missing requestor (the prototype defaults to
   *    user=PLUGIN and an org id from sessionStorage / empty).
   * 2. Build JobModelDTO headers from the requestor's prM/vaL pairs plus
   *    REQUESTED_DATE / JOB_TITLE / (ORGANIZATIONSID if known) / optional
   *    INSTRUCTIONS / optional DUE_DATE. (No PRIORITY / TURNAROUND — Engage
   *    rejects those, and the prototype never sends them.)
   * 3. POST to CreateInternal; on transport failure fall back to /Create,
   *    exactly like the prototype.
   */
  const submitJob = useCallback(
    async (input: JobSubmitInput): Promise<JobSubmitResult> => {
      setSubmitting(true);
      setError(null);

      // Reject a past due date up front with a clear, specific message before
      // touching Engage — otherwise the server rejects it opaquely and the UI
      // falls back to the misleading generic error (AIVA-6847).
      const dueDateError = validateDueDate(input.dueDate);
      if (dueDateError) {
        setError(dueDateError);
        setSubmitting(false);
        throw new Error(dueDateError);
      }

      try {
        // Engage identifies a user by their COGNITO email (passed as
        // requestorEmail / postedByEmail). The M365/Office `userEmail` is a
        // different account (e.g. …@post.williamslea.co.uk) that Engage does not
        // recognise, so it must never be the identity sent to Engage's per-user
        // endpoints. Prefer the Cognito identity for the CreateFallback user,
        // keeping userEmail only as a last-resort POSTED_BY fallback before the
        // "PLUGIN" default (AIVA-7885).
        const user = input.postedByEmail || input.requestorEmail || input.userEmail || "PLUGIN";
        // Requestor lookup uses the Cognito email ONLY. Including the M365
        // `userEmail` here is what made GetRequestorByEmail fire against the wrong
        // (…@post.williamslea.co.uk) account (AIVA-7885).
        const emailsToTry = [input.requestorEmail]
          .map((e) => (e ?? "").trim())
          .filter((e, i, arr) => e.length > 0 && arr.indexOf(e) === i);

        const requestor = await resolveRequestor(client, input.site, input.jobType, emailsToTry);
        const storedOrg = readMigratedSessionPref(ORG_STORAGE_KEY) ?? "";
        const orgId = requestor ? String(requestor.organizationsid ?? "") : storedOrg;

        const jobModel: EngageJobModel = {
          headers: buildJobHeaders(requestor, orgId, input),
          categories: [],
          attachments: [],
        };

        const resp = await createJob(client, input, orgId, user, jobModel);

        const jobNo = pickJobNo(resp);
        if (!jobNo) {
          const apiMsg = pickErrorMessage(resp);
          throw new Error(apiMsg ?? "Engage did not return a job number");
        }

        // Attach the exported document/presentation text to the created job via
        // a SEPARATE SaveAttachments call (the create body carries none — same as
        // the prototype). Best-effort: reported via the result, never thrown.
        // POSTED_BY must be the Engage Cognito user, not the M365 account.
        const postedBy = input.postedByEmail || user;
        const { attachmentsSaved, attachmentError } = await uploadAttachments(
          client,
          jobNo,
          postedBy,
          input.attachments ?? [],
          input.uploadToDrive,
        );

        logConnectorSuccess("engage", "submit-job");
        return { jobNo, sla: pickSla(resp), attachmentsSaved, attachmentError };
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Submit failed";
        setError(msg);
        logConnectorFailure("engage", e, "submit-job");
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [client],
  );

  return {
    connected,
    sites,
    selectedSite,
    setSelectedSite,
    jobTypes,
    loadingSites,
    loadingJobTypes,
    submitting,
    error,
    submitJob,
    reloadSites,
  };
}

/* ── Tracked-job polling (mirrors prototype's 30s status check) ── */

const POLL_INTERVAL_MS = 30_000;
const TRACKED_JOBS_KEY = "aiva_engage_tracked_jobs";

function readTrackedJobs(): string[] {
  try {
    const raw = readMigratedSessionPref(TRACKED_JOBS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((j): j is string => typeof j === "string") : [];
  } catch {
    return [];
  }
}

function writeTrackedJobs(jobs: string[]): void {
  try {
    globalThis.sessionStorage?.setItem(TRACKED_JOBS_KEY, JSON.stringify(jobs));
  } catch {
    // sessionStorage unavailable — silently ignore
  }
}

export function useEngageJobTracking(site: string) {
  const adapter = useConnectionAdapter("engage");
  const client = useEngageClient();
  const [trackedJobs, setTrackedJobs] = useState<string[]>(() => readTrackedJobs());
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const [jobs, setJobs] = useState<EngageJob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const trackJob = useCallback((jobNo: string) => {
    setTrackedJobs((prev) => {
      if (prev.includes(jobNo)) return prev;
      const next = [...prev, jobNo];
      writeTrackedJobs(next);
      return next;
    });
  }, []);

  const untrackJob = useCallback((jobNo: string) => {
    setTrackedJobs((prev) => {
      const next = prev.filter((j) => j !== jobNo);
      writeTrackedJobs(next);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!adapter?.isConnected() || !site || trackedJobs.length === 0) return;
    const next: EngageJob[] = [];
    const statuses: Record<string, string> = {};
    for (const jobNo of trackedJobs) {
      try {
        const resp = await client.getJob(site, jobNo);
        const job: EngageJob | null =
          resp && typeof resp === "object" && "result" in resp
            ? (resp as { result: EngageJob }).result
            : resp;
        if (job) {
          next.push(job);
          const status =
            job.statuS_NAME ||
            job.status_name ||
            job.status ||
            job.stagE_NAME ||
            job.stage_name ||
            "";
          statuses[jobNo] = status;
        }
      } catch {
        // skip transient failures — next tick will retry
      }
    }
    setJobs(next);
    setStatusMap(statuses);
  }, [adapter, client, site, trackedJobs]);

  useEffect(() => {
    if (!adapter?.isConnected() || !site) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    refresh().catch(() => undefined);
    intervalRef.current = setInterval(() => {
      refresh().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [adapter, site, refresh]);

  return { trackedJobs, jobs, statusMap, trackJob, untrackJob, refresh };
}
